import { useCallback, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { Input, Tab, Tabs, Textarea } from '@nextui-org/react';
import { useGetAiworkersPresignedUrlMutation, useUploadFileToS3Mutation } from '@circuitry-ai/doc-data';
import { CtyButton } from '../button/CtyButton';
import { CtyShowToast } from '../toast/CtyToast';
import { ClaimIntakeFileUploadCard } from './ClaimIntakeFileUploadCard';
import { ClaimSubmitSuccessModal } from './ClaimSubmitSuccessModal';
import { runClaimIntakeOperation, type ClaimIntakeSession } from './claimIntakeAgentApi';
import { uploadClaimIntakeFilesViaPresigned } from './claimIntakeRum';
import { sanitizeVinInput, VIN_SHORT_LENGTH } from './claimIntakeUtils';
import { CLAIM_INTAKE_GENERIC_ERROR_MESSAGE } from './claimSubmitErrorUtils';
import {
  buildAdditionalSourcesFromGenerateResponse,
  removeAdditionalSourcesForFile,
  type AdditionalSourceItem,
} from './additionalInformationUpload';

export type ExistingClaimPhase = 'identify' | 'refine' | 'documents';

export type ExistingClaimItem = Readonly<{
  assignedToEmail: string;
  claimNumber: string;
  contractNumber: string;
  servicerName: string;
}>;

type ExistingClaimTabKey = 'claim-lookup' | 'additional-information';

type ExistingClaimLookupParams = Readonly<{
  vin: string;
  lastName: string;
  mileage: string;
}>;

type RefineField = 'lastName' | 'mileage';

type ExistingClaimIntakePanelProps = Readonly<{
  phase: ExistingClaimPhase;
  onPhaseChange: (phase: ExistingClaimPhase) => void;
  onBackToNewClaim: () => void;
  onSubmitSuccess?: () => void;
  session: ClaimIntakeSession | null;
  agentsBaseUrl?: string;
  token: string | null;
  lookupSourceId?: string;
  onAiworkerUsageIdChange?: (id: string | null) => void;
  aiworkerUsageId?: string | null;
}>;

function isSameUploadedFile(a: File, b: File): boolean {
  return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
}

function readStringField(row: Record<string, unknown>, key: string): string {
  const value = row[key];
  return typeof value === 'string' ? value.trim() : '';
}

function canRunExistingClaimLookup(vin: string, lastName: string, mileage: string): boolean {
  return (
    vin.trim().length === VIN_SHORT_LENGTH &&
    (lastName.trim().length > 0 || mileage.trim().length > 0)
  );
}

function parseMileageValue(mileage: string): number | null {
  const trimmed = mileage.trim();
  if (!trimmed) return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function missingRefineField(params: ExistingClaimLookupParams): RefineField | null {
  if (!params.lastName.trim()) return 'lastName';
  if (!params.mileage.trim()) return 'mileage';
  return null;
}

function refineFieldLabel(field: RefineField): string {
  return field === 'lastName' ? "customer's last name" : 'mileage at time of claim';
}

/** Normalizes existing_claim outputs — prefers `claims` (live API), also accepts `text` or a raw array. */
export function parseExistingClaimsFromResponse(data: unknown): ExistingClaimItem[] {
  let rows: unknown = data;
  if (data != null && typeof data === 'object' && !Array.isArray(data)) {
    const record = data as Record<string, unknown>;
    if (Array.isArray(record.claims)) rows = record.claims;
    else if (Array.isArray(record.text)) rows = record.text;
  }
  if (!Array.isArray(rows)) return [];

  return rows.flatMap((row) => {
    if (row == null || typeof row !== 'object' || Array.isArray(row)) return [];
    const record = row as Record<string, unknown>;
    const claimNumber = readStringField(record, 'claimNumber');
    if (!claimNumber) return [];
    return [
      {
        assignedToEmail: readStringField(record, 'assignedToEmail'),
        claimNumber,
        contractNumber: readStringField(record, 'contractNumber'),
        servicerName: readStringField(record, 'servicerName'),
      },
    ];
  });
}

function phaseToTab(phase: ExistingClaimPhase): ExistingClaimTabKey {
  return phase === 'documents' ? 'additional-information' : 'claim-lookup';
}

function tabToPhase(tab: ExistingClaimTabKey, isRefining: boolean): ExistingClaimPhase {
  if (tab === 'additional-information') return 'documents';
  return isRefining ? 'refine' : 'identify';
}

async function runExistingClaimLookup(
  params: ExistingClaimLookupParams,
  options: Readonly<{
    session: ClaimIntakeSession;
    token: string;
    lookupSourceId?: string;
    agentsBaseUrl?: string;
    aiworkerUsageId?: string | null;
    onAiworkerUsageIdChange?: (id: string | null) => void;
  }>
): Promise<ExistingClaimItem[]> {
  const mileageValue = parseMileageValue(params.mileage);
  if (params.mileage.trim() && mileageValue == null) {
    throw new Error('INVALID_MILEAGE');
  }

  const chainedUsageId = options.aiworkerUsageId?.trim() || undefined;
  const { data: result, aiworkerUsageId } = await runClaimIntakeOperation<unknown>({
    session: options.session,
    event: 'existing_claim',
    userInput: {
      source_id: options.lookupSourceId,
      payload: {
        query_parameters: {
          vin: params.vin.trim(),
          lastName: params.lastName.trim(),
          mileage: mileageValue,
        },
      },
    },
    token: options.token,
    baseUrl: options.agentsBaseUrl,
    ...(chainedUsageId ? { aiworkerUsageId: chainedUsageId } : {}),
  });

  if (aiworkerUsageId) options.onAiworkerUsageIdChange?.(aiworkerUsageId);
  return parseExistingClaimsFromResponse(result);
}

function ExistingClaimIdentifyPanel({
  onBackToNewClaim,
  onLookup,
  loading,
  error,
}: Readonly<{
  onBackToNewClaim: () => void;
  onLookup: (params: ExistingClaimLookupParams) => void;
  loading: boolean;
  error: string | null;
}>) {
  const [vinLast8, setVinLast8] = useState('');
  const [lastName, setLastName] = useState('');
  const [mileage, setMileage] = useState('');

  const handleVinChange = useCallback((value: string) => {
    setVinLast8(sanitizeVinInput(value).slice(0, VIN_SHORT_LENGTH));
  }, []);

  const canLookup = canRunExistingClaimLookup(vinLast8, lastName, mileage);

  return (
    <div className="bg-content1 rounded-xl border border-default-200 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <p className="text-sm text-default-500">
          Answer a couple of questions to access the claim
        </p>
        <CtyButton size="sm" variant="bordered" color="primary" onPress={onBackToNewClaim}>
          Start a new claim instead
        </CtyButton>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2 mt-4">
        <Input
          label="What is the last 8 of the VIN?"
          value={vinLast8}
          onValueChange={handleVinChange}
          maxLength={VIN_SHORT_LENGTH}
          variant="bordered"
          size="lg"
          isRequired
          isDisabled={loading}
          classNames={{ inputWrapper: 'border-default-300' }}
        />
        <Input
          label="Customer's Last Name?"
          value={lastName}
          onValueChange={setLastName}
          variant="bordered"
          size="lg"
          isDisabled={loading}
          isRequired
          classNames={{ inputWrapper: 'border-default-300' }}
        />
      </div>

      {error ? (
        <div className="mt-4 p-3 bg-danger/10 border border-danger/40 rounded-lg text-sm text-danger">
          {error}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3 mt-4">
        <CtyButton
          color="primary"
          isLoading={loading}
          onPress={() =>
            onLookup({ vin: vinLast8.trim(), lastName: lastName.trim(), mileage: mileage.trim() })
          }
          isDisabled={loading || !canLookup}
          startContent={!loading && <Icon icon="mdi:magnify" width={18} />}
          className="font-semibold"
        >
          Access Claim
        </CtyButton>
      </div>
    </div>
  );
}

function ExistingClaimRefinePanel({
  refineField,
  lookupParams,
  onBack,
  onLookup,
  loading,
  error,
}: Readonly<{
  refineField: RefineField;
  lookupParams: ExistingClaimLookupParams;
  onBack: () => void;
  onLookup: (params: ExistingClaimLookupParams) => void;
  loading: boolean;
  error: string | null;
}>) {
  const [lastName, setLastName] = useState(lookupParams.lastName);
  const [mileage, setMileage] = useState(lookupParams.mileage);

  const fieldLabel = refineFieldLabel(refineField);
  const canContinue =
    refineField === 'lastName' ? lastName.trim().length > 0 : mileage.trim().length > 0;

  return (
    <div className="bg-content1 rounded-xl border border-default-200 p-4">
      <p className="text-sm text-foreground mb-4">
        We found more than one contract. Could you please provide the {fieldLabel}?
      </p>

      {refineField === 'lastName' ? (
        <Input
          label="Customer's Last Name?"
          value={lastName}
          onValueChange={setLastName}
          variant="bordered"
          size="lg"
          isDisabled={loading}
          isRequired
          classNames={{ inputWrapper: 'border-default-300' }}
        />
      ) : (
        <Input
          label="Mileage at Time of Claim?"
          value={mileage}
          onValueChange={(v) => setMileage(v.replace(/[^\d]/gu, ''))}
          variant="bordered"
          size="lg"
          isDisabled={loading}
          isRequired
          classNames={{ inputWrapper: 'border-default-300' }}
        />
      )}

      {error ? (
        <div className="mt-4 p-3 bg-danger/10 border border-danger/40 rounded-lg text-sm text-danger">
          {error}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
        <CtyButton variant="flat" onPress={onBack} isDisabled={loading}>
          Back
        </CtyButton>
        <CtyButton
          color="primary"
          className="font-semibold"
          isLoading={loading}
          isDisabled={loading || !canContinue}
          onPress={() =>
            onLookup({
              vin: lookupParams.vin,
              lastName: lastName.trim(),
              mileage: mileage.trim(),
            })
          }
          endContent={!loading && <Icon icon="mdi:arrow-right" width={18} />}
        >
          Continue
        </CtyButton>
      </div>
    </div>
  );
}

function readSubmitStatusMessage(data: unknown): string {
  if (typeof data === 'string' && data.trim()) return data.trim();
  if (data != null && typeof data === 'object' && !Array.isArray(data)) {
    const text = (data as Record<string, unknown>).text;
    if (typeof text === 'string' && text.trim()) return text.trim();
  }
  return 'Your supporting information has been submitted.';
}

function ExistingClaimDocumentsPanel({
  matchedClaims,
  onBack,
  onSubmitSuccess,
  session,
  agentsBaseUrl,
  token,
  aiworkerUsageId,
  onAiworkerUsageIdChange,
}: Readonly<{
  matchedClaims: ExistingClaimItem[];
  onBack: () => void;
  onSubmitSuccess?: () => void;
  session: ClaimIntakeSession | null;
  agentsBaseUrl?: string;
  token: string | null;
  aiworkerUsageId?: string | null;
  onAiworkerUsageIdChange?: (id: string | null) => void;
}>) {
  const [files, setFiles] = useState<File[]>([]);
  const [sources, setSources] = useState<AdditionalSourceItem[]>([]);
  const [questionText, setQuestionText] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccessMessage, setSubmitSuccessMessage] = useState<string | null>(null);
  const [submitSuccessModalOpen, setSubmitSuccessModalOpen] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [getAiworkersPresignedUrl] = useGetAiworkersPresignedUrlMutation();
  const [uploadToS3] = useUploadFileToS3Mutation();
  const isSubmitted = Boolean(submitSuccessMessage);
  const hasQuestion = questionText.trim().length > 0;
  const hasSupportingInput = sources.length > 0 || hasQuestion;

  const processFiles = useCallback(
    async (incomingFiles: File[]) => {
      const newFiles = incomingFiles.filter(
        (file) => !files.some((existing) => isSameUploadedFile(existing, file))
      );
      if (newFiles.length === 0) return;

      setIsUploading(true);
      setUploadError(null);
      try {
        const { urlList } = await uploadClaimIntakeFilesViaPresigned({
          files: newFiles,
          purpose: 'existing_claim_docs',
          sessionId: session?.sessionId,
          aiworkerUsageId,
          getPresignedUrls: (payload) => getAiworkersPresignedUrl(payload).unwrap(),
          uploadOne: (file, presignedUrl) =>
            uploadToS3({ file, presignedUrl, CtyShowToast }).unwrap(),
        });

        const newSources = buildAdditionalSourcesFromGenerateResponse(urlList, newFiles);
        setFiles((prev) => [...prev, ...newFiles]);
        setSources((prev) => [...prev, ...newSources]);
        CtyShowToast('success', `${newFiles.length} file(s) uploaded.`);
      } catch {
        setUploadError(CLAIM_INTAKE_GENERIC_ERROR_MESSAGE);
        CtyShowToast('error', CLAIM_INTAKE_GENERIC_ERROR_MESSAGE);
      } finally {
        setIsUploading(false);
      }
    },
    [files, getAiworkersPresignedUrl, uploadToS3]
  );

  const isMultipleClaims = matchedClaims.length > 1;
  const singleClaim = matchedClaims.length === 1 ? matchedClaims[0] : null;

  const handleSubmit = useCallback(async () => {
    if (!hasSupportingInput) {
      CtyShowToast('error', 'Please upload a supporting document or enter a question.');
      return;
    }
    if (!token?.trim() || !session) {
      CtyShowToast('error', 'Service not ready yet — please try again in a moment.');
      return;
    }
    if (matchedClaims.length === 0) {
      CtyShowToast('error', 'No claim matched — please look up the claim again.');
      return;
    }

    setIsSubmitting(true);
    try {
      const chainedUsageId = aiworkerUsageId?.trim() || undefined;
      const trimmedQuery = questionText.trim();
      const userInput =
        matchedClaims.length > 1
          ? {
              sources,
              query: trimmedQuery,
              claims: matchedClaims.map((claim) => ({
                claimNumber: claim.claimNumber,
                contractNumber: claim.contractNumber,
                servicerName: claim.servicerName,
                assignedToEmail: claim.assignedToEmail,
              })),
            }
          : {
              sources,
              query: trimmedQuery,
              assignedToEmail: singleClaim?.assignedToEmail ?? '',
              claimNumber: singleClaim?.claimNumber ?? '',
              contractNumber: singleClaim?.contractNumber ?? '',
              servicerName: singleClaim?.servicerName ?? '',
            };

      const { data, aiworkerUsageId: submitUsageId } = await runClaimIntakeOperation({
        session,
        event: matchedClaims.length > 1 ? 'multiple_claims' : 'query',
        userInput,
        token,
        baseUrl: agentsBaseUrl,
        ...(chainedUsageId ? { aiworkerUsageId: chainedUsageId } : {}),
      });
      if (submitUsageId) onAiworkerUsageIdChange?.(submitUsageId);
      setSubmitSuccessMessage(readSubmitStatusMessage(data));
      setSubmitSuccessModalOpen(true);
    } catch {
      CtyShowToast('error', CLAIM_INTAKE_GENERIC_ERROR_MESSAGE);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    hasSupportingInput,
    questionText,
    sources,
    token,
    session,
    agentsBaseUrl,
    aiworkerUsageId,
    matchedClaims,
    singleClaim,
    onAiworkerUsageIdChange,
  ]);

  const handleStartNewClaim = useCallback(() => {
    setSubmitSuccessModalOpen(false);
    onSubmitSuccess?.();
  }, [onSubmitSuccess]);

  const handlePrimaryAction = useCallback(() => {
    if (isSubmitted) {
      handleStartNewClaim();
      return;
    }
    void handleSubmit();
  }, [isSubmitted, handleStartNewClaim, handleSubmit]);

  return (
    <div className="space-y-4 sm:space-y-6">
      <ClaimSubmitSuccessModal
        isOpen={submitSuccessModalOpen}
        claimNumber={null}
        showClaimNumber={false}
        title="Information Submitted"
        message={submitSuccessMessage ?? 'Your supporting information has been submitted.'}
        onClose={() => setSubmitSuccessModalOpen(false)}
        onStartNewClaim={handleStartNewClaim}
      />

      <div className="bg-success/10 border border-success/40 rounded-lg p-3 sm:p-4">
        <p className="text-sm font-semibold text-success">
          {isMultipleClaims ? 'We found matching claims' : 'We found your claim'}
        </p>
        <p className="text-sm text-default-600 mt-1">
          Upload a supporting document or ask a question below.
        </p>
      </div>

      <ClaimIntakeFileUploadCard
        title="Document Upload"
        badgeLabel="Optional"
        isUploading={isUploading}
        isDisabled={isSubmitted}
        loadingMessage="Uploading files…"
        idleTitle="Click to drop a repair estimate, invoice, image, or video"
        idleHint="PDF, JPG, PNG, MP4 — multiple files supported"
        accept=".pdf,.jpg,.jpeg,.png,.mp4"
        uploadError={uploadError}
        uploadedFiles={files}
        onFileDrop={(e) => {
          e.preventDefault();
          if (isUploading || isSubmitted) return;
          void processFiles(Array.from(e.dataTransfer.files ?? []));
        }}
        onFileSelect={(e) => {
          const chosen = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (chosen.length > 0 && !isSubmitted) void processFiles(chosen);
        }}
        onPickFile={() => fileInputRef.current?.click()}
        onRemoveFile={(index) => {
          if (isSubmitted) return;
          const file = files[index];
          if (!file) return;
          setFiles((prev) => prev.filter((_, i) => i !== index));
          setSources((prev) => removeAdditionalSourcesForFile(prev, file));
        }}
        fileInputRef={fileInputRef}
        footerExtra={
          <div className="mt-3">
            <Textarea
              label="I have a question regarding the claim"
              value={questionText}
              onValueChange={setQuestionText}
              minRows={3}
              variant="bordered"
              isDisabled={isUploading || isSubmitting || isSubmitted}
              classNames={{ inputWrapper: 'border-default-300' }}
            />
          </div>
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <CtyButton
          variant="flat"
          onPress={onBack}
          isDisabled={isUploading || isSubmitting || isSubmitted}
        >
          Back
        </CtyButton>
        <CtyButton
          color="primary"
          className="font-semibold"
          isLoading={isSubmitting}
          isDisabled={isUploading || isSubmitting || (!isSubmitted && !hasSupportingInput)}
          onPress={handlePrimaryAction}
        >
          {isSubmitted ? 'Start New Claim' : 'Submit Information'}
        </CtyButton>
      </div>
    </div>
  );
}

export function ExistingClaimIntakePanel({
  phase,
  onPhaseChange,
  onBackToNewClaim,
  onSubmitSuccess,
  session,
  agentsBaseUrl,
  token,
  lookupSourceId,
  onAiworkerUsageIdChange,
  aiworkerUsageId,
}: ExistingClaimIntakePanelProps) {
  const [lookupParams, setLookupParams] = useState<ExistingClaimLookupParams | null>(null);
  const [refineField, setRefineField] = useState<RefineField | null>(null);
  const [matchedClaims, setMatchedClaims] = useState<ExistingClaimItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const activeTab = phaseToTab(phase);
  const additionalInfoDisabled = matchedClaims.length === 0;

  const handleSubmitSuccess = useCallback(() => {
    setLookupParams(null);
    setRefineField(null);
    setMatchedClaims([]);
    setLookupError(null);
    onSubmitSuccess?.();
  }, [onSubmitSuccess]);

  const resolveClaims = useCallback(
    async (params: ExistingClaimLookupParams) => {
      if (!token?.trim() || !session) {
        setLookupError('Service not ready yet — please try again in a moment.');
        return;
      }

      setLoading(true);
      setLookupError(null);
      setLookupParams(params);

      try {
        const claims = await runExistingClaimLookup(params, {
          session,
          token,
          lookupSourceId,
          agentsBaseUrl,
          aiworkerUsageId,
          onAiworkerUsageIdChange,
        });

        if (claims.length === 0) {
          setLookupError('No claims found for the details provided.');
          return;
        }

        if (claims.length === 1) {
          setMatchedClaims(claims);
          setRefineField(null);
          onPhaseChange('documents');
          return;
        }

        // More than one match: ask for the missing optional field when available.
        const fieldToAsk = missingRefineField(params);
        if (fieldToAsk) {
          setRefineField(fieldToAsk);
          onPhaseChange('refine');
          return;
        }

        // All three fields already provided and still multiple → documents + multiple_claims submit.
        setMatchedClaims(claims);
        setRefineField(null);
        onPhaseChange('documents');
      } catch (err: unknown) {
        if (err instanceof Error && err.message === 'INVALID_MILEAGE') {
          setLookupError('Please enter a valid mileage.');
          return;
        }
        console.warn('[ExistingClaimIntake] existing_claim failed', err);
        setLookupError(CLAIM_INTAKE_GENERIC_ERROR_MESSAGE);
        CtyShowToast('error', CLAIM_INTAKE_GENERIC_ERROR_MESSAGE);
      } finally {
        setLoading(false);
      }
    },
    [
      token,
      session,
      lookupSourceId,
      agentsBaseUrl,
      aiworkerUsageId,
      onAiworkerUsageIdChange,
      onPhaseChange,
    ]
  );

  const handleTabChange = useCallback(
    (key: React.Key) => {
      const tab = String(key) as ExistingClaimTabKey;
      if (tab === 'additional-information' && matchedClaims.length === 0) return;
      onPhaseChange(tabToPhase(tab, phase === 'refine'));
    },
    [matchedClaims.length, phase, onPhaseChange]
  );

  const handleRefineBack = useCallback(() => {
    setRefineField(null);
    setLookupError(null);
    onPhaseChange('identify');
  }, [onPhaseChange]);

  const handleDocumentsBack = useCallback(() => {
    setMatchedClaims([]);
    setLookupError(null);
    if (refineField) {
      onPhaseChange('refine');
      return;
    }
    onPhaseChange('identify');
  }, [refineField, onPhaseChange]);

  return (
    <div className="space-y-4">
      <Tabs
        selectedKey={activeTab}
        onSelectionChange={handleTabChange}
        aria-label="Existing claim sections"
        variant="underlined"
        color="primary"
        classNames={{
          tabList: 'gap-4 w-full relative rounded-none p-0 border-b border-divider',
          cursor: 'w-full bg-primary',
          tab: 'max-w-fit px-0 h-10',
          tabContent: 'group-data-[selected=true]:text-primary font-semibold',
        }}
      >
        <Tab key="claim-lookup" title="Claim Lookup">
          <div className="pt-4 space-y-4">
            {phase === 'refine' && refineField && lookupParams ? (
              <ExistingClaimRefinePanel
                refineField={refineField}
                lookupParams={lookupParams}
                onBack={handleRefineBack}
                onLookup={resolveClaims}
                loading={loading}
                error={lookupError}
              />
            ) : (
              <ExistingClaimIdentifyPanel
                onBackToNewClaim={onBackToNewClaim}
                onLookup={resolveClaims}
                loading={loading}
                error={lookupError}
              />
            )}
          </div>
        </Tab>
        <Tab
          key="additional-information"
          title="Additional Information"
          isDisabled={additionalInfoDisabled}
        >
          <div className="pt-4">
            {matchedClaims.length > 0 ? (
              <ExistingClaimDocumentsPanel
                matchedClaims={matchedClaims}
                session={session}
                agentsBaseUrl={agentsBaseUrl}
                token={token}
                aiworkerUsageId={aiworkerUsageId}
                onAiworkerUsageIdChange={onAiworkerUsageIdChange}
                onSubmitSuccess={handleSubmitSuccess}
                onBack={handleDocumentsBack}
              />
            ) : null}
          </div>
        </Tab>
      </Tabs>
    </div>
  );
}
